import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import { home_data } from 'src/stores/account/user';
import { save_home } from 'src/helpers/home_helpers';
import { ENERGY_DASHBOARD_ID, ENERGY_MONITORING_DASHBOARD_TAB } from './constants';

export type EnergyDashboardVisual = {
	name?: string;
	icon?: string;
	backgroundColor?: string;
};

const HOME_DATA_KEY = 'energyMonitoringDashboardVisuals';

const ROUTE_SEGMENT_BY_ID: Record<number, string> = {
	[ENERGY_DASHBOARD_ID]: 'energy_monitoring',
};

const ROUTE_SEGMENT_TO_ID: Record<string, number> = {
	energy_monitoring: ENERGY_DASHBOARD_ID,
	'-103': ENERGY_DASHBOARD_ID,
};

const TAB_META_BY_ID = {
	[ENERGY_DASHBOARD_ID]: ENERGY_MONITORING_DASHBOARD_TAB,
} as const;

export const getEnergyDashboardIdFromRouteSegment = (
	segment: string | null | undefined,
): number | null => {
	if (!segment) return null;
	return ROUTE_SEGMENT_TO_ID[segment] ?? null;
};

export const getEnergyDashboardRouteSegment = (dashboardId: number): string | null =>
	ROUTE_SEGMENT_BY_ID[dashboardId] ?? null;

const defaultTabLabel = (dashboardId: number) => {
	const meta = TAB_META_BY_ID[dashboardId as keyof typeof TAB_META_BY_ID];
	if (!meta) return 'Energy Monitoring';
	return get(format)(meta.text, { default: meta.defaultText });
};

const defaultBackgroundColor = (dashboardId: number) =>
	TAB_META_BY_ID[dashboardId as keyof typeof TAB_META_BY_ID]?.backgroundColor?.trim() || 'color-0';

export const getEnergyDashboardVisualSettings = (
	dashboardId: number,
	rawHomeData: Record<string, unknown> | null | undefined = get(home_data),
) => {
	const meta = TAB_META_BY_ID[dashboardId as keyof typeof TAB_META_BY_ID];
	const visuals = rawHomeData?.[HOME_DATA_KEY] as Record<string, EnergyDashboardVisual> | undefined;
	const stored = visuals?.[String(dashboardId)] ?? {};

	return {
		name: defaultTabLabel(dashboardId),
		icon: stored.icon?.trim() || meta?.icon || 'fas fa-bolt',
		backgroundColor: stored.backgroundColor?.trim() || defaultBackgroundColor(dashboardId),
	};
};

export const saveEnergyDashboardVisualSettings = async (
	dashboardId: number,
	visual: { icon: string; backgroundColor: string },
) => {
	const currentHomeData = get(home_data) ?? {};
	const currentVisuals = currentHomeData[HOME_DATA_KEY] ?? {};
	const nextVisuals = {
		...currentVisuals,
		[String(dashboardId)]: {
			icon: visual.icon.trim(),
			backgroundColor: visual.backgroundColor.trim() || defaultBackgroundColor(dashboardId),
		},
	};

	await save_home(HOME_DATA_KEY, nextVisuals);
	home_data.set({
		...currentHomeData,
		[HOME_DATA_KEY]: nextVisuals,
	});
};

export const applyEnergyDashboardVisualToTab = <
	T extends {
		id?: number;
		text?: string;
		icon?: string;
		backgroundColor?: string;
	},
>(
	tab: T,
	rawHomeData: Record<string, unknown> | null | undefined,
): T => {
	if (tab.id === undefined || tab.id === null) return tab;
	const visual = getEnergyDashboardVisualSettings(tab.id, rawHomeData);
	return {
		...tab,
		text: visual.name,
		icon: visual.icon,
		backgroundColor: visual.backgroundColor,
	};
};
